// Founder-side "Your advisor" card. Phase 1.2 of advisor pipe.
// docs/founder-advisor-pipe-prd.md §8.1 + §16 Q6 hard rule.
//
// Trust invariants (NEVER violate, §16 Q6):
//   - No breach/at-risk state. The whole point is to make the founder feel
//     seen, not to dunk on the advisor.
//   - No mention of internal notes or 'manual_marked' touches. The server
//     already filters by visible_to_founder, but defense-in-depth: this
//     component never renders a missing-coverage state as "your advisor is
//     missing" — it shows a calm "will be assigned soon".
//
// Used on:
//   - founder-home.tsx (default founder portal)
//   - founder-1to10/command-center.tsx (1to10 cohort theme)

import { useEffect, useState } from "react";
import { CheckCircle2, UserCircle2 } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

interface AdvisorPayload {
  advisor: { id: number; name: string; title: string; avatarUrl: string | null; bio: string | null } | null;
  lastTouch: { at: string; action: string; artifactKind: string } | null;
  visibleTouchesLast30d: number;
}
interface ActivityPayload {
  activity: Array<{ id: number; bankerId: number; action: string; artifactKind: string; createdAt: string }>;
  advisorsById: Record<number, { name: string; avatarUrl: string | null }>;
}

function fmtAt(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { dateStyle: "medium" });
}
function friendlyKind(k: string): string {
  switch (k) {
    case "weekly_update": return "your check-in";
    case "proof_artifact": return "a proof item";
    case "claim_review": return "a claim review";
    case "company": return "your profile";
    case "meeting": return "a meeting note";
    case "note": return "a note";
    case "email_thread": return "an email thread";
    default: return k.replace(/_/g, " ");
  }
}

export interface AdvisorCardProps {
  /** "light" or "dark" — drives only background palette, not trust content. */
  theme?: "light" | "dark";
}

export default function AdvisorCard({ theme = "light" }: AdvisorCardProps) {
  const [data, setData] = useState<AdvisorPayload | null>(null);
  const [activity, setActivity] = useState<ActivityPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("founder_token") || "";
    if (!token) { setLoading(false); return; }
    let cancelled = false;
    Promise.all([
      fetch(`${BASE}api/founder/my-advisor`, { headers: { "x-founder-token": token } })
        .then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${BASE}api/founder/advisor-activity`, { headers: { "x-founder-token": token } })
        .then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([a, b]) => {
      if (cancelled) return;
      setData(a);
      setActivity(b);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const isDark = theme === "dark";
  const containerCls = isDark
    ? "rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5"
    : "rounded-2xl border bg-card p-5 shadow-sm";
  const textMuted = isDark ? "text-white/55" : "text-muted-foreground";
  const textPrimary = isDark ? "text-white" : "text-foreground";

  if (loading) {
    return (
      <div className={containerCls}>
        <div className={`text-xs uppercase tracking-wider ${textMuted}`}>Your advisor</div>
        <div className={`mt-2 h-12 ${isDark ? "bg-white/[0.05]" : "bg-muted"} rounded animate-pulse`} />
      </div>
    );
  }

  if (!data || !data.advisor) {
    return (
      <div className={containerCls}>
        <div className={`text-xs uppercase tracking-wider ${textMuted}`}>Your advisor</div>
        <div className="mt-2 flex items-center gap-3">
          <UserCircle2 className={`w-10 h-10 ${textMuted}`} />
          <div>
            <p className={`text-sm font-medium ${textPrimary}`}>Will be assigned soon</p>
            <p className={`text-xs mt-0.5 ${textMuted}`}>Your Favcy advisor will reach out shortly.</p>
          </div>
        </div>
      </div>
    );
  }

  const a = data.advisor;
  const initials = a.name.split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase();
  const recentActivity = activity?.activity ?? [];

  return (
    <div className={containerCls}>
      <div className={`text-xs uppercase tracking-wider ${textMuted}`}>Your advisor</div>
      <div className="mt-3 flex items-start gap-3">
        {a.avatarUrl ? (
          <img src={a.avatarUrl} alt={a.name} className="w-12 h-12 rounded-full object-cover border" />
        ) : (
          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold text-sm ${isDark ? "bg-[#FF5C00]/[0.15] text-[#FF5C00] border border-[#FF5C00]/30" : "bg-primary/10 text-primary border border-primary/20"}`}>
            {initials}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className={`text-base font-semibold ${textPrimary}`}>{a.name}</p>
          <p className={`text-xs ${textMuted}`}>{a.title}</p>
          {a.bio && <p className={`text-xs mt-1 ${textMuted} line-clamp-2`}>{a.bio}</p>}
        </div>
      </div>

      {data.lastTouch ? (
        <div className={`mt-3 flex items-center gap-2 text-xs ${textPrimary}`}>
          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
          <span>
            Reviewed {friendlyKind(data.lastTouch.artifactKind)} on {fmtAt(data.lastTouch.at)}
            <span className={`ml-1 ${textMuted}`}>· {data.visibleTouchesLast30d} reviews in last 30 days</span>
          </span>
        </div>
      ) : (
        <p className={`mt-3 text-xs ${textMuted}`}>No advisor activity yet — they'll reach out soon.</p>
      )}

      {recentActivity.length > 0 && (
        <details className="mt-3">
          <summary className={`text-xs cursor-pointer ${textMuted} hover:underline`}>Activity log ({recentActivity.length})</summary>
          <ul className="mt-2 space-y-1.5">
            {recentActivity.map(item => {
              const advisor = activity?.advisorsById[item.bankerId];
              return (
                <li key={item.id} className={`text-xs flex items-start gap-2 ${textPrimary}`}>
                  <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>
                    {advisor?.name ?? "Your advisor"} reviewed {friendlyKind(item.artifactKind)}{" "}
                    <span className={textMuted}>· {fmtAt(item.createdAt)}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </div>
  );
}
